import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../ui/Card.jsx';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
import { updateUserDetails } from '../../services/api';

function UserFormModal({ isOpen, onClose, user, onSuccess }) {
    const initialFocusRef = useRef(null);

    const [formData, setFormData] = useState({
        nome: '',
        email: '',
        grauAcademico: '', // NOVO CAMPO
        instituicao: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen && user) {
            setFormData({
                nome: user.nome || '',
                email: user.email || '',
                grauAcademico: user.grauAcademico || '', // ATUALIZADO
                instituicao: user.instituicao || '',
            });
            setError('');
            
            setTimeout(() => {
                if (initialFocusRef.current) {
                    initialFocusRef.current.focus();
                }
            }, 100);
        }
    }, [isOpen, user]);

    if (!isOpen) {
        return null;
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        try {
            const payload = {
                nome: formData.nome,
                email: formData.email,
                grauAcademico: formData.grauAcademico, // INCLUÍDO
                instituicao: formData.instituicao,
            };
            await updateUserDetails(user.id, payload);
            toast.success(`Usuário "${formData.nome}" atualizado com sucesso!`);
            onSuccess();
        } catch (err) {
            console.error('Erro ao atualizar usuário:', err);
            setError(err.message || 'Ocorreu um erro ao tentar atualizar o usuário.');
            toast.error(err.message || 'Erro ao atualizar usuário.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div 
            className="fixed inset-0 bg-black/60 z-50 flex justify-center items-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-user-title"
        >
            <Card className="w-full max-w-md relative">
                <button 
                    onClick={onClose} 
                    className="absolute top-4 right-4 p-1 rounded-full hover:bg-accent transition-colors"
                    aria-label="Fechar"
                >
                    <X className="h-5 w-5" />
                </button>
                <CardHeader>
                    <CardTitle id="edit-user-title" className="mb-2">Editar Usuário</CardTitle>
                    <CardDescription className="leading-relaxed">
                        Atualize os detalhes do usuário.
                    </CardDescription>
                </CardHeader>
                <form onSubmit={handleSubmit}>
                    <CardContent className="space-y-4">
                        {error && <p className="text-sm font-medium text-destructive">{error}</p>}

                        <div className="grid gap-2">
                            <Label htmlFor="nome" className="mb-1">Nome Completo</Label>
                            <Input 
                                id="nome" 
                                name="nome" 
                                value={formData.nome} 
                                onChange={handleChange} 
                                required 
                                disabled={isSubmitting} 
                                ref={initialFocusRef} 
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="email" className="mb-1">Email</Label>
                            <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required disabled={isSubmitting} />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="grauAcademico" className="mb-1">Grau Acadêmico (Opcional)</Label> {/* NOVO CAMPO */}
                            <Input id="grauAcademico" name="grauAcademico" value={formData.grauAcademico} onChange={handleChange} disabled={isSubmitting} />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="instituicao" className="mb-1">Instituição (Opcional)</Label>
                            <Input id="instituicao" name="instituicao" value={formData.instituicao} onChange={handleChange} disabled={isSubmitting} />
                        </div>
                    </CardContent>
                    <CardFooter className="flex flex-col sm:flex-row justify-end gap-2">
                        <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting} className="w-full sm:w-auto">
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
                            {isSubmitting ? 'A guardar...' : 'Guardar Alterações'}
                        </Button>
                    </CardFooter>
                </form>
            </Card>
        </div>
    );
}

export default UserFormModal;